import React, { useState } from 'react';
import { habitService, CreateHabitData } from '../../services/habitService';
import './AddHabitModal.css';

interface AddHabitModalProps {
  userId: number;
  onClose: () => void;
  onHabitAdded: () => void;
  onError: (error: string) => void;
}

const AddHabitModal: React.FC<AddHabitModalProps> = ({ 
  userId, 
  onClose, 
  onHabitAdded,
  onError 
}) => {
  const [newHabitName, setNewHabitName] = useState('');
  const [newHabitIcon, setNewHabitIcon] = useState('✅');
  const [newHabitCategory, setNewHabitCategory] = useState('other');
  const [newHabitFrequencyType, setNewHabitFrequencyType] = useState<'daily' | 'every_x_days' | 'weekly' | 'monthly' | 'custom_interval'>('daily');
  const [newHabitFrequencyValue, setNewHabitFrequencyValue] = useState(1);
  const [newHabitTrackingType, setNewHabitTrackingType] = useState<'boolean' | 'count' | 'measurement' | 'both'>('boolean');
  const [newHabitTargetCount, setNewHabitTargetCount] = useState<number | undefined>(undefined);
  const [newHabitTargetMeasurement, setNewHabitTargetMeasurement] = useState<number | undefined>(undefined);
  const [newHabitMeasurementUnit, setNewHabitMeasurementUnit] = useState<string>('');
  const [isCreating, setIsCreating] = useState(false);

  const commonIcons = ['✅', '💊', '🧘', '📚', '📖', '🚶', '🏃', '💪', '🥗', '💧', '😴', '🎯'];

  const categories = [
    { value: 'health', label: 'Health' },
    { value: 'fitness', label: 'Fitness' },
    { value: 'education', label: 'Education' },
    { value: 'productivity', label: 'Productivity' },
    { value: 'mindfulness', label: 'Mindfulness' },
    { value: 'social', label: 'Social' },
    { value: 'hobbies', label: 'Hobbies' },
    { value: 'self_care', label: 'Self Care' },
    { value: 'nutrition', label: 'Nutrition' },
    { value: 'sleep', label: 'Sleep' },
    { value: 'other', label: 'Other' }
  ];

  const resetForm = () => {
    setNewHabitName('');
    setNewHabitIcon('✅');
    setNewHabitCategory('other');
    setNewHabitFrequencyType('daily');
    setNewHabitFrequencyValue(1);
    setNewHabitTrackingType('boolean');
    setNewHabitTargetCount(undefined);
    setNewHabitTargetMeasurement(undefined);
    setNewHabitMeasurementUnit('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const addNewHabit = async () => {
    if (!newHabitName.trim()) return;

    setIsCreating(true);
    try {
      const habitData: CreateHabitData = {
        user_id: userId,
        name: newHabitName.trim(),
        icon: newHabitIcon,
        category: newHabitCategory,
        tracking_type: newHabitTrackingType,
        frequency_type: newHabitFrequencyType,
        frequency_value: newHabitFrequencyValue,
        frequency_unit: newHabitFrequencyType === 'weekly' ? 'times_per_week' : 
                        newHabitFrequencyType === 'monthly' ? 'times_per_month' : 'days',
        target_count: newHabitTargetCount,
        target_measurement: newHabitTargetMeasurement,
        measurement_unit: newHabitMeasurementUnit || undefined,
        start_date: habitService.formatDateForAPI(new Date())
      };
      
      const response = await habitService.createHabit(habitData);
      
      if (response.success && response.id) {
        resetForm();
        onClose();
        onHabitAdded();
      } else {
        onError(response.message || 'Failed to create habit');
      }
    } catch (err) {
      console.error('Error creating habit:', err);
      onError('Failed to create habit. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="add-habit-modal-overlay" onClick={handleClose}>
      <div className="add-habit-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="add-habit-modal-header">
          <h3>Add New Habit</h3>
          <button className="add-habit-close-btn" onClick={handleClose}>×</button>
        </div>
        <div className="add-habit-modal-body">
          <div className="add-habit-form-group">
            <label>Habit Name</label>
            <input
              type="text"
              value={newHabitName}
              onChange={(e) => setNewHabitName(e.target.value)}
              placeholder="Enter habit name..."
              autoFocus
            />
          </div>
          <div className="add-habit-form-group">
            <label>Category</label>
            <select
              value={newHabitCategory}
              onChange={(e) => setNewHabitCategory(e.target.value)}
            >
              {categories.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
          <div className="add-habit-form-group">
            <label>Tracking Type</label>
            <select
              value={newHabitTrackingType}
              onChange={(e) => setNewHabitTrackingType(e.target.value as 'boolean' | 'count' | 'measurement' | 'both')}
            >
              <option value="boolean">Yes/No (Completed or not)</option>
              <option value="count">Count (Number of times)</option>
              <option value="measurement">Measurement (Distance, duration, etc.)</option>
              <option value="both">Both (Count and measurement)</option>
            </select>
            <small className="add-habit-form-hint">
              {newHabitTrackingType === 'boolean' && 'Track whether the habit was completed'}
              {newHabitTrackingType === 'count' && 'Track the number of times (e.g., exercises, repetitions)'}
              {newHabitTrackingType === 'measurement' && 'Track a measurement value (e.g., km run, minutes exercised)'}
              {newHabitTrackingType === 'both' && 'Track both count and measurement (e.g., 3 sets of 10 reps)'}
            </small>
          </div>

          {(newHabitTrackingType === 'count' || newHabitTrackingType === 'both') && (
            <div className="add-habit-form-group">
              <label>Target Count (Optional)</label>
              <input
                type="number"
                value={newHabitTargetCount || ''}
                onChange={(e) => setNewHabitTargetCount(e.target.value ? Number(e.target.value) : undefined)}
                min="1"
                placeholder="e.g., 10"
              />
              <small className="add-habit-form-hint">Daily target for count (leave empty if no target)</small>
            </div>
          )}

          {(newHabitTrackingType === 'measurement' || newHabitTrackingType === 'both') && (
            <>
              <div className="add-habit-form-group">
                <label>Measurement Unit</label>
                <select
                  value={newHabitMeasurementUnit}
                  onChange={(e) => setNewHabitMeasurementUnit(e.target.value)}
                >
                  <option value="">Select unit...</option>
                  <option value="km">km</option>
                  <option value="miles">miles</option>
                  <option value="minutes">minutes</option>
                  <option value="hours">hours</option>
                  <option value="reps">reps</option>
                  <option value="sets">sets</option>
                  <option value="kg">kg</option>
                  <option value="lbs">lbs</option>
                  <option value="pages">pages</option>
                  <option value="glasses">glasses</option>
                  <option value="cups">cups</option>
                </select>
              </div>
              <div className="add-habit-form-group">
                <label>Target Measurement (Optional)</label>
                <input
                  type="number"
                  value={newHabitTargetMeasurement || ''}
                  onChange={(e) => setNewHabitTargetMeasurement(e.target.value ? Number(e.target.value) : undefined)}
                  min="0"
                  step={newHabitMeasurementUnit === 'km' || newHabitMeasurementUnit === 'miles' ? '0.1' : '1'}
                  placeholder={`e.g., 5 ${newHabitMeasurementUnit || ''}`}
                />
                <small className="add-habit-form-hint">Daily target for measurement (leave empty if no target)</small>
              </div>
            </>
          )}

          <div className="add-habit-form-group">
            <label>Frequency</label>
            <div className="add-habit-frequency-selector">
              <select
                value={newHabitFrequencyType}
                onChange={(e) => setNewHabitFrequencyType(e.target.value as 'daily' | 'every_x_days' | 'weekly' | 'monthly' | 'custom_interval')}
              >
                <option value="daily">Every day</option>
                <option value="every_x_days">Every X days</option>
                <option value="weekly">X times per week</option>
                <option value="monthly">X times per month</option>
                <option value="custom_interval">X times in Y days</option>
              </select>
              <div className="add-habit-frequency-value-input">
                {newHabitFrequencyType !== 'daily' && (
                  <input
                    type="number"
                    value={newHabitFrequencyValue}
                    onChange={(e) => setNewHabitFrequencyValue(Number(e.target.value))}
                    min="1"
                    max={newHabitFrequencyType === 'every_x_days' ? 365 : newHabitFrequencyType === 'weekly' ? 7 : newHabitFrequencyType === 'monthly' ? 31 : 365}
                    placeholder="1"
                  />
                )}
                <span className="add-habit-frequency-hint">
                  {newHabitFrequencyType === 'daily' && 'Every day'}
                  {newHabitFrequencyType === 'every_x_days' && 'days interval'}
                  {newHabitFrequencyType === 'weekly' && 'times per week'}
                  {newHabitFrequencyType === 'monthly' && 'times per month'}
                  {newHabitFrequencyType === 'custom_interval' && 'times in X days'}
                </span>
              </div>
            </div>
            <small className="add-habit-form-hint">
              {newHabitFrequencyType === 'daily' && 'This habit will be tracked every day'}
              {newHabitFrequencyType === 'every_x_days' && 'e.g., 3 = every 3 days'}
              {newHabitFrequencyType === 'weekly' && 'e.g., 3 = three times per week'}
              {newHabitFrequencyType === 'monthly' && 'e.g., 4 = four times per month'}
              {newHabitFrequencyType === 'custom_interval' && 'e.g., 2 = twice within the specified days'}
            </small>
          </div>
          <div className="add-habit-form-group">
            <label>Icon</label>
            <div className="add-habit-icon-selector">
              {commonIcons.map((icon) => (
                <button
                  key={icon}
                  type="button"
                  className={`add-habit-icon-option ${newHabitIcon === icon ? 'selected' : ''}`}
                  onClick={() => setNewHabitIcon(icon)}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="add-habit-modal-footer">
          <button className="add-habit-cancel-btn" onClick={handleClose} disabled={isCreating}>
            Cancel
          </button>
          <button 
            className="add-habit-save-btn" 
            onClick={addNewHabit} 
            disabled={!newHabitName.trim() || isCreating}
          >
            {isCreating ? 'Creating...' : 'Add Habit'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddHabitModal;

